/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import classNames from 'classnames';
import React from 'react';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';
import Box from 'carbon-react/lib/components/box';
import Typography from 'carbon-react/lib/components/typography';

import { TermEstimate } from '@futrli-sdk/billing';
import { AnyError } from '@futrli/errors';
import { formatMinorUnitsAmount, FormattedMessage, WrappedComponentProps, injectIntl } from '@futrli/localisation';
import { ProductSlug } from '@futrli/shared-services';

import styles from './coupon-form-field.scss';
import { fetchEstimateWithCoupons } from '../../../core/api';
import { INTEGRATION_LEVEL } from '../../../core/constants';
import { BillingTerm } from '../../../core/types';
import { textFieldValidator } from '../../../utils/form-validator-utils';

interface CouponFieldState {
  value: string;
  error: AnyError;
  isChecking: boolean;
}

interface OwnProps {
  classes?: Partial<{
    container: string;
    field: string;
  }>;
  workspaceId: string;
  subscriptionTerm: BillingTerm;
  productSlug: ProductSlug;
  buttonFormat?: 'button' | 'link';
  hideHelperText?: boolean;
  existingCustomer: boolean;
  onComplete: (coupon: string, termEstimate: TermEstimate) => void;
  onClean?: () => void;
  onChecking?: (value: boolean) => void;
  disabled?: boolean;
  placeholder?: string;
}
type Props = OwnProps & WrappedComponentProps;

const initialState: CouponFieldState = {
  error: null,
  value: '',
  isChecking: false
};

/**
 * Coupon form field component.
 * @param {Props} props
 * @returns CouponFormField.
 */
export const CouponFormFieldComponent = ({
  classes,
  disabled: defaultDisabled = false,
  placeholder,
  subscriptionTerm,
  intl,
  onComplete,
  onChecking,
  workspaceId,
  productSlug,
  onClean,
  buttonFormat = 'button',
  existingCustomer
}: Props): JSX.Element => {
  const [couponState, setCouponState] = React.useState<CouponFieldState>({ ...initialState });
  const [termEstimate, setTermEstimate] = React.useState<TermEstimate | null>(null);
  const isCouponApplied = Boolean(termEstimate);
  const isCouponFieldDisabled = defaultDisabled || isCouponApplied || couponState.isChecking;
  const hasInvalidCouponLength = Boolean(couponState.value) && !textFieldValidator(couponState.value);
  const isInvalidCoupon = hasInvalidCouponLength || Boolean(couponState.error);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value;

    setCouponState(prevState => ({
      ...prevState,
      value,
      error: null
    }));
  };

  const handleOnRemoveAppliedCoupon = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setCouponState({ ...initialState });
    setTermEstimate(null);
    onClean?.();
  };

  const handleOnApplyCoupon = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    try {
      onChecking?.(true);
      setCouponState(prevState => ({
        ...prevState,
        isChecking: true,
        error: null
      }));

      const termEstimate = await fetchEstimateWithCoupons(workspaceId, productSlug, INTEGRATION_LEVEL, {
        term: subscriptionTerm,
        coupon_ids: [couponState.value],
        existing_customer: existingCustomer
      });

      setTermEstimate(termEstimate);
      setCouponState(prevState => ({
        ...prevState,
        isChecking: false
      }));
      onComplete(couponState.value, termEstimate);
    } catch (error) {
      setCouponState(prevState => ({
        ...prevState,
        error,
        isChecking: false
      }));
    } finally {
      onChecking?.(false);
    }
  };

  const onClick = !isCouponApplied ? handleOnApplyCoupon : handleOnRemoveAppliedCoupon;

  return (
    <div className={classNames(styles.couponFieldContainer, classes?.container)}>
      <Box width="100%">
        <Textbox
          value={couponState.value}
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: 'billing.coupon_field_placeholder',
              defaultMessage: 'If you have a coupon code, enter it here'
            })
          }
          onChange={handleOnChange}
          disabled={isCouponFieldDisabled}
          error={
            isInvalidCoupon ? getHelperText(termEstimate, isInvalidCoupon, hasInvalidCouponLength, intl) : undefined
          }
          mb={0}
          size="large"
          mr="24px"
        />
        {isCouponApplied && termEstimate && (
          <Typography fontSize="12px" mb="0px">
            <FormattedMessage
              id="billing.your_discount_has_been_applied"
              defaultMessage="Your discount has been applied! The new amount you'll be charged is {amount} (excluding tax)."
              values={{ amount: formatMinorUnitsAmount(termEstimate.estimated_cost, termEstimate.currency_code, intl) }}
            />
          </Typography>
        )}
      </Box>
      {getSubmitButton(buttonFormat, couponState, termEstimate, onClick)}
    </div>
  );
};
export const CouponFormField = injectIntl(CouponFormFieldComponent);

export function getSubmitButton(
  buttonFormat: Props['buttonFormat'],
  couponState: CouponFieldState,
  termEstimate: TermEstimate | null,
  onClick: (event: React.MouseEvent<HTMLElement>) => void
): JSX.Element {
  const isCouponApplied = Boolean(termEstimate);
  const isCouponBtnDisabled =
    !couponState.value ||
    couponState.isChecking ||
    (Boolean(couponState.value) && !textFieldValidator(couponState.value));

  if (buttonFormat === 'button') {
    return (
      <Box minWidth={isCouponApplied ? 190 : 170}>
        <Button disabled={isCouponBtnDisabled} buttonType="primary" onClick={onClick} size="large">
          {couponState.isChecking && (
            <Box minWidth={100}>
              <LoaderSpinner showSpinnerLabel={false} size="small" />
            </Box>
          )}
          {!couponState.isChecking && (
            <React.Fragment>
              {isCouponApplied ? (
                <FormattedMessage id="billing.remove_coupon" defaultMessage="Remove coupon" />
              ) : (
                <FormattedMessage id="billing.apply_coupon" defaultMessage="Apply coupon" />
              )}
            </React.Fragment>
          )}
        </Button>
      </Box>
    );
  }

  return (
    <React.Fragment>
      {couponState.isChecking && <LoaderSpinner />}
      {!couponState.isChecking && (
        <a
          onClick={!isCouponBtnDisabled ? onClick : undefined}
          className={classNames(styles.link, { [styles.disabled]: isCouponBtnDisabled })}
        >
          {isCouponApplied ? (
            <FormattedMessage id="billing.remove_coupon" defaultMessage="Remove coupon" />
          ) : (
            <FormattedMessage id="billing.apply_coupon" defaultMessage="Apply coupon" />
          )}
        </a>
      )}
    </React.Fragment>
  );
}

export function getHelperText(
  termEstimate: TermEstimate | null,
  isInvalidCoupon: boolean,
  hasInvalidCouponLength: boolean,
  intl: Props['intl']
): string | undefined {
  if (hasInvalidCouponLength) {
    return intl.formatMessage({
      id: 'validation.we_only_allow_up_to_100_characters',
      defaultMessage: 'Oops, we only allow up to 100 characters.'
    });
  }

  if (isInvalidCoupon) {
    return intl.formatMessage({
      id: 'billing.we_dont_recognize_this_coupon',
      defaultMessage: "Sorry, we don't recognize this coupon."
    });
  }

  if (!termEstimate) {
    return;
  }
}
